import { Fragment, type ReactElement } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Skeleton, Text, Tooltip } from 'components/ui';
import type { ResolvedMetric } from '../../../dashboardData.types';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../../SummaryDashboard.constants';
import { tileBadgeSx } from '../DashboardGrid.styles';
import {
  comparisonCaption,
  formatAbsoluteChange,
  formatMetricValue,
  formatRelativeChange,
  hasValue,
  resolveMetricLabel,
  resolveNumberFormat,
  shouldShowRelativeChange,
} from '../dashboardValue.helpers';
import type { TotalsCardProps } from './TotalsCard.props';
import {
  absoluteChangeSx,
  accentBarSx,
  cardTitleSx,
  emptyCaptionSx,
  emptyValueSx,
  footerSx,
  getTotalsVariant,
  metricBlockSx,
  metricGroupSx,
  metricLabelSx,
  metricListLabelSx,
  metricListSx,
  metricListValueSx,
  metricTextStackSx,
  metricValueSx,
  relativeChangeSx,
  secondaryMetricValueSx,
  skeletonStackSx,
  totalsBodySx,
  totalsCardSx,
  type TotalsVariant,
  trendIconSx,
} from './TotalsCard.styles';

const EMPTY_VALUE = '—';
const ERROR_CAPTION = 'Data unavailable';
const NOT_AGGREGATABLE_CAPTION = 'Not available at firm level';
// Past this a hero number per metric is illegible at the grid's 168px row height, so the card
// switches to a plain label/value list.
const MAX_HERO_METRICS = 3;

const trendDirection = (change: number): 'up' | 'down' | 'flat' => {
  if (change > 0) {
    return 'up';
  }
  return change < 0 ? 'down' : 'flat';
};

interface MetricViewProps {
  metric: ResolvedMetric;
  configuredMetric: string;
  variant: TotalsVariant;
  currencyCode: string;
  isSingleMetric: boolean;
  showAccentBar: boolean;
  title?: string;
  numberFormat: ReturnType<typeof resolveNumberFormat>;
  isHero: boolean;
}

const MetricBlock = ({
  metric,
  configuredMetric,
  variant,
  currencyCode,
  isSingleMetric,
  showAccentBar,
  title,
  numberFormat,
  isHero,
}: MetricViewProps): ReactElement => {
  const label = resolveMetricLabel({
    fallbackLabel: metric?.label,
    isSingleMetric,
    metric: configuredMetric,
    title,
  });

  // 0 is a real total, so presence is tested with isNil rather than truthiness.
  const isResolved = hasValue(metric?.value);
  const isUnsupported = metric?.aggregation === 'unsupported';

  return (
    <Box sx={metricBlockSx}>
      {showAccentBar && <Box sx={accentBarSx} />}
      <Box sx={metricTextStackSx}>
        <Text
          component="p"
          data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardLabel}
          sx={metricLabelSx(variant)}>
          {label}
        </Text>
        {isResolved ? (
          <Text
            component="p"
            data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardValue}
            sx={isHero ? metricValueSx(variant) : secondaryMetricValueSx}>
            {formatMetricValue({
              currencyCode,
              format: numberFormat,
              value: metric.value as number,
            })}
          </Text>
        ) : (
          <Tooltip title={isUnsupported ? (metric?.unsupportedReason ?? '') : ''}>
            <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardEmpty}>
              <Text component="p" sx={emptyValueSx}>
                {EMPTY_VALUE}
              </Text>
              {isUnsupported && (
                <Text component="p" sx={emptyCaptionSx}>
                  {NOT_AGGREGATABLE_CAPTION}
                </Text>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

interface FooterProps {
  metric: ResolvedMetric;
  variant: TotalsVariant;
  currencyCode: string;
  showTrendIcon: boolean;
  numberFormat: ReturnType<typeof resolveNumberFormat>;
}

const ComparisonFooter = ({
  metric,
  variant,
  currencyCode,
  showTrendIcon,
  numberFormat,
}: FooterProps): ReactElement | null => {
  const comparison = metric?.comparison;
  if (!comparison || !hasValue(metric?.value)) {
    return null;
  }

  const { absoluteChange, relativeChange, mode } = comparison;
  if (!hasValue(absoluteChange)) {
    return null;
  }

  const caption = comparisonCaption(mode);
  // A currency card carries both lines; a multiplier or percent card carries only the absolute
  // one, which then takes the arrow and the colour (see totals_2.png).
  const showRelative = shouldShowRelativeChange(numberFormat) && hasValue(relativeChange);
  const direction = trendDirection(showRelative ? (relativeChange as number) : absoluteChange);
  const TrendIcon = direction === 'down' ? ArrowDownwardIcon : ArrowUpwardIcon;
  const trendIcon =
    showTrendIcon && direction !== 'flat' ? <TrendIcon sx={trendIconSx} /> : null;

  const absoluteText = `${formatAbsoluteChange({
    currencyCode,
    format: numberFormat,
    value: absoluteChange,
  })} ${caption}`;

  return (
    <Box sx={footerSx(variant)}>
      {showRelative ? (
        <>
          <Text
            component="span"
            data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardChange}
            sx={absoluteChangeSx}>
            {absoluteText}
          </Text>
          <Text
            component="span"
            data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardPercentChange}
            sx={relativeChangeSx(direction)}>
            {trendIcon}
            {formatRelativeChange(relativeChange as number)}
          </Text>
        </>
      ) : (
        <Text
          component="span"
          data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardChange}
          sx={relativeChangeSx(direction)}>
          {trendIcon}
          {absoluteText}
        </Text>
      )}
    </Box>
  );
};

/**
 * KPI value card for a `totals` component.
 *
 * Renders the whole tile rather than a body slot: the mockups have no header row, so the type chip
 * and field chips the generic card shows are deliberately absent here. `display.highlight` is
 * dropped too — it is an AI-authored string, and next to a computed figure it would either
 * duplicate or contradict it.
 */
const TotalsCard = ({
  component,
  data,
  currencyCode = 'USD',
  isLoading = false,
  isError = false,
}: TotalsCardProps): ReactElement => {
  const { display = {}, fields, styling = {}, position } = component;
  const variant = getTotalsVariant(position);
  const configuredMetrics = fields?.metrics ?? [];
  const isSingleMetric = configuredMetrics.length === 1;
  const showAccentBar = styling.accent_bar === true;
  const showTrendIcon = styling.show_trend_icon !== false;

  const resolvedFor = (name: string): ResolvedMetric | undefined =>
    data?.metrics.find(entry => entry.metric === name);

  const formatFor = (name: string) =>
    resolveNumberFormat({ isSingleMetric, metric: name, styling });

  const body = (() => {
    if (isLoading) {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardLoading} sx={skeletonStackSx}>
          <Skeleton height={18} variant="rect" width="45%" />
          <Skeleton height={44} variant="rect" width="70%" />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardError}>
          <Text component="p" sx={emptyValueSx}>
            {EMPTY_VALUE}
          </Text>
          <Text component="p" sx={emptyCaptionSx}>
            {ERROR_CAPTION}
          </Text>
        </Box>
      );
    }

    // 4+ metrics: a compact list. Four hero numbers do not fit the grid's minimum row height.
    if (configuredMetrics.length > MAX_HERO_METRICS) {
      return (
        <Box sx={metricListSx}>
          {configuredMetrics.map(name => {
            const metric = resolvedFor(name);
            return (
              <Fragment key={name}>
                <Text component="span" sx={metricListLabelSx}>
                  {resolveMetricLabel({
                    fallbackLabel: metric?.label,
                    isSingleMetric: false,
                    metric: name,
                  })}
                </Text>
                <Text
                  component="span"
                  data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCardValue}
                  sx={metricListValueSx}>
                  {hasValue(metric?.value)
                    ? formatMetricValue({
                      currencyCode,
                      format: formatFor(name),
                      value: metric.value as number,
                    })
                    : EMPTY_VALUE}
                </Text>
              </Fragment>
            );
          })}
        </Box>
      );
    }

    // 2-3 metrics: demoted blocks side by side when the tile is wide enough.
    if (configuredMetrics.length > 1) {
      return (
        <Box sx={metricGroupSx(configuredMetrics.length)}>
          {configuredMetrics.map(name => (
            <MetricBlock
              configuredMetric={name}
              currencyCode={currencyCode}
              isHero={false}
              isSingleMetric={false}
              key={name}
              metric={resolvedFor(name) as ResolvedMetric}
              numberFormat={formatFor(name)}
              showAccentBar={showAccentBar}
              variant={variant}
            />
          ))}
        </Box>
      );
    }

    const name = configuredMetrics[0];
    return (
      <MetricBlock
        configuredMetric={name}
        currencyCode={currencyCode}
        isHero
        isSingleMetric
        metric={resolvedFor(name) as ResolvedMetric}
        numberFormat={formatFor(name)}
        showAccentBar={showAccentBar}
        title={display.title}
        variant={variant}
      />
    );
  })();

  const heroMetric = isSingleMetric ? resolvedFor(configuredMetrics[0]) : undefined;
  const showFooter = !isLoading && !isError && Boolean(heroMetric);

  return (
    <Box
      data-component-type="totals"
      data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCard}
      id={component.id}
      sx={totalsCardSx(variant)}>
      {Boolean(display.badge?.text) && (
        <Box component="span" sx={tileBadgeSx}>
          {display.badge?.text}
        </Box>
      )}

      {/* With more than one metric the title names the card, not any single figure. */}
      {!isSingleMetric && Boolean(display.title) && (
        <Text
          component="p"
          data-testid={SUMMARY_DASHBOARD_TEST_IDS.componentCardTitle}
          sx={cardTitleSx}>
          {display.title}
        </Text>
      )}

      <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.totalsCard} sx={totalsBodySx(variant)}>
        {body}
        {showFooter && (
          <ComparisonFooter
            currencyCode={currencyCode}
            metric={heroMetric as ResolvedMetric}
            numberFormat={formatFor(configuredMetrics[0])}
            showTrendIcon={showTrendIcon}
            variant={variant}
          />
        )}
      </Box>
    </Box>
  );
};

export default TotalsCard;
